import { SSG_REAL_ESTATE } from '@dreamstack/accentro-contentful/src/config'
import { useIsLoggedInWithLoading } from '@dreamstack/auth'
import { useParams } from '@dreamstack/feature-components/src/hooks/useParams'
import { MainLayout } from '@dreamstack/feature-components/src/layout/MainLayout'
import { HeaderImage } from '@dreamstack/feature-components/src/property/HeaderImage'
import { getFilteredPropOrProjectImages } from '@dreamstack/feature-components/src/property/helper/utils'
import { PropertyDetail } from '@dreamstack/feature-components/src/property/PropertyDetail'
import { transformPropstackImageUrl } from '@dreamstack/feature-components/src/property/propstack-image-url-transform'
import { getCityUrlPart } from '@dreamstack/feature-components/src/property/routing/getCityUrlPart'
import { getPropertyOtherLocaleUrls } from '@dreamstack/feature-components/src/property/routing/getPropertyUrl'
import type { LocaleString } from '@dreamstack/i18n'
import { OtherLocaleUrlsContextProvider, useLocale } from '@dreamstack/i18n'
import { getAllAvailableProperties } from '@dreamstack/real-estate-logic'
import { trackPageViewForProperty } from '@dreamstack/recommendation'
import { DreamstackSeo, PropertyRichMarkup } from '@dreamstack/seo'
import { AdditionalTrackingInfoContextProvider } from '@dreamstack/tracking'
import { trpc } from '@dreamstack/trpc/client'
import { PROPERTY_STATUS_FOLLOW_INDEX } from '@dreamstack/real-estate-logic'
import 'keen-slider/keen-slider.min.css'
import first from 'lodash/first'
import includes from 'lodash/includes'
import map from 'lodash/map'
import type { FunctionComponent } from 'react'
import { useEffect } from 'react'
import { z } from 'zod'
import { getStaticPathsPlus } from '../../../lib/getStaticPathsPlus'
import { getStaticPropsPlus } from '../../../lib/getStaticPropsPlus'
import { getPropertyPreviewById } from '../../../lib/prisma/properties'

const paramSchema = z.object({ propId: z.coerce.number() })

const PropertyDetails: FunctionComponent<
  React.PropsWithChildren<unknown>
> = () => {
  const params = useParams()
  const parsedParams = paramSchema.safeParse(params)

  const id = parsedParams.success ? parsedParams.data.propId : null

  const locale = useLocale()
  const { data: property } = trpc.property.getPropertyById.useQuery(
    { id: id!, locale },
    { trpc: { ssr: true }, staleTime: Infinity, enabled: !!id }
  )
  const { mutateAsync: addPropertyEngagement } =
    trpc.analytics.addPropertyEngagement.useMutation()

  const { loading, isLoggedIn } = useIsLoggedInWithLoading()
  const propTitle = property?.title
  const imageUrl = first(property?.property_images)?.url
  const { filteredImages } = getFilteredPropOrProjectImages(
    property?.property_images ?? []
  )
  const objectHasNoImage = !imageUrl
  const characterCount = propTitle?.length
  const thumbImageUrl = transformPropstackImageUrl({
    url: imageUrl,
    size: 'thumb',
  })
  const followIndex = includes(PROPERTY_STATUS_FOLLOW_INDEX, property?.status)

  useEffect(() => {
    if (!id || loading) return
    if (isLoggedIn) {
      addPropertyEngagement({
        propertyId: id,
        engagementType: 'view',
        locale: locale,
      })
    } else {
      trackPageViewForProperty({ id })
    }
  }, [addPropertyEngagement, id, isLoggedIn, loading, locale])

  if (!id) return null

  const propInfo = {
    property_id: id,
    title: propTitle,
    property_address: property?.address,
  }

  return (
    <>
      <OtherLocaleUrlsContextProvider
        context={getPropertyOtherLocaleUrls({
          id,
          city: property?.city ?? undefined,
        })}
      >
        <AdditionalTrackingInfoContextProvider context={propInfo}>
          <DreamstackSeo
            title={propTitle ?? undefined}
            description={property?.description_note ?? undefined}
            image={thumbImageUrl ? { url: thumbImageUrl } : undefined}
            nofollow={!followIndex}
            noindex={!followIndex}
          />
          <PropertyRichMarkup id={id} property={property} />
          <HeaderImage
            title={propTitle ?? ''}
            image={filteredImages[0]}
            smallFont
            isObject
            characterCount={characterCount}
          />
          <MainLayout
            navbarProps={{
              forceDarkTextColor: false,
              isProjectAndProperty: true,
            }}
          >
            <PropertyDetail id={id} />
          </MainLayout>
          {!!objectHasNoImage && (
            <MainLayout
              navbarProps={{
                forceDarkTextColor: true,
                isProjectAndProperty: true,
              }}
            >
              <PropertyDetail id={id} />
            </MainLayout>
          )}
        </AdditionalTrackingInfoContextProvider>
      </OtherLocaleUrlsContextProvider>
    </>
  )
}

export const getStaticProps = getStaticPropsPlus({
  getProps: async ({ params, locale }) => {
    const paramSchema = z.object({ propId: z.coerce.number() })
    const parsedParams = paramSchema.safeParse(params)
    if (!parsedParams.success) {
      return { notFound: true }
    }
    const id = parsedParams.data.propId

    const property = await getPropertyPreviewById({
      id,
      locale: (locale as LocaleString) ?? 'de',
    })

    const city = property?.city

    // TODO: if city does not match return 404? why tho?
    const notFound = !property

    if (notFound) {
      // TODO: not found page suggestion redirect instead of direct redirect
      return {
        notFound: true,
      }
    }

    return {
      notFound: notFound || undefined,
    }
  },
})

export const getStaticPaths = getStaticPathsPlus({
  getPaths: async () => {
    if (!SSG_REAL_ESTATE) {
      return []
    }

    const validProperties = await getAllAvailableProperties()

    return map(validProperties, (prop) => {
      if (!prop.city) {
        // should never happen, because they are filtered before
        throw new Error(`No city defined`)
      }

      return {
        params: {
          propId: prop.id.toString(),
          slug: getCityUrlPart(prop.city),
        },
      }
    })
  },
})

export default PropertyDetails
